import { useId } from 'react';
import { DASHBOARD_STATUSES, statusLabels, type DashboardStatus } from '../dashboardTypes';
import { filtersActive, type DashboardFilters } from '../filterModel';

/**
 * Filters the server supports today: Status, Tax Type / Form (941, 940) and a Due Date range. The design's other fields (Tax Year, Tax Period, Frequency,
 * Agency / Jurisdiction, Tax Type, Payroll Processor, Payment Partner, Branch / Work Location, Payment Origin, Company, Process No Later Than, Date Paid) have no
 * backend filter yet. They are shown disabled with one clear note rather than faked by filtering only the page already loaded.
 */
const UNAVAILABLE: ReadonlyArray<string> = [
  'Tax Year', 'Tax Period', 'Frequency', 'Agency / Jurisdiction', 'Payroll Processor', 'Payment Partner', 'Branch / Work Location', 'Payment Origin',
  'Process No Later Than', 'Date Paid',
];

export function DashboardFiltersBar({ filters, onChange, onClear, moreOpen, onToggleMore, summaryHidden, onToggleSummary, disabled }: {
  filters: DashboardFilters;
  onChange: (next: DashboardFilters) => void;
  onClear: () => void;
  moreOpen: boolean;
  onToggleMore: () => void;
  summaryHidden: boolean;
  onToggleSummary: () => void;
  disabled: boolean;
}) {
  const uid = useId();
  const active = filtersActive(filters);
  const panelId = `${uid}-more`;
  return (
    <div className="tpd-filters">
      <div className="tpd-toolbar" role="search" aria-label="Filter payments">
        <button type="button" className="tpd-btn tpd-btn--ghost tpd-toggle-summary" onClick={onToggleSummary} aria-pressed={summaryHidden}>
          {summaryHidden ? 'Show Summary and Queue' : 'Hide Summary and Queue'}
        </button>
        <label className="tpd-field">
          <span>Tax Type / Form</span>
          <select value={filters.formCode} disabled={disabled} onChange={(e) => onChange({ ...filters, formCode: e.target.value })}>
            <option value="">All</option>
            <option value="Form941">Form 941</option>
            <option value="Form940">Form 940</option>
          </select>
        </label>
        <label className="tpd-field">
          <span>Status</span>
          <select value={filters.status} disabled={disabled} onChange={(e) => onChange({ ...filters, status: e.target.value as DashboardStatus | '' })}>
            <option value="">All</option>
            {DASHBOARD_STATUSES.map((s) => <option key={s} value={s}>{statusLabels[s]}</option>)}
          </select>
        </label>
        <button type="button" className="tpd-btn tpd-btn--outline" aria-expanded={moreOpen} aria-controls={panelId} onClick={onToggleMore}>
          {moreOpen ? 'See Less' : 'More Filters'}
        </button>
        <button type="button" className="tpd-btn tpd-btn--outline" onClick={onClear} disabled={!active}>Clear Filter</button>
      </div>
      {moreOpen && (
        <div id={panelId} className="tpd-more" role="group" aria-label="More filters">
          <div className="tpd-more__row">
            <label className="tpd-field">
              <span>Due Date from</span>
              <input type="date" value={filters.dueFrom} max={filters.dueTo || undefined} disabled={disabled} onChange={(e) => onChange({ ...filters, dueFrom: e.target.value })} />
            </label>
            <label className="tpd-field">
              <span>Due Date to</span>
              <input type="date" value={filters.dueTo} min={filters.dueFrom || undefined} disabled={disabled} onChange={(e) => onChange({ ...filters, dueTo: e.target.value })} />
            </label>
          </div>
          <fieldset className="tpd-more__unavailable" disabled>
            <legend>Not available yet</legend>
            <p className="tpd-muted">These filters need data the server does not hold yet, so they cannot be used. Nothing is filtered by them.</p>
            <div className="tpd-more__row">
              {UNAVAILABLE.map((label) => (
                <label key={label} className="tpd-field">
                  <span>{label}</span>
                  <select disabled><option>Not available yet</option></select>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </div>
  );
}
